import { useSelector } from 'react-redux'
import React, { useMemo, useState } from 'react'
import { useAppDispatch } from '@user/store/hooks'
import {
  IUserPreferenceGeneralListItemType,
  saveGeneralPreferences,
  userGeneralPreferencesList,
} from '@user/store/userPreferences/userPreferencesGeneral'
import { GridDensity, GridState } from '@mui/x-data-grid-pro'
import {
  selectWorkWaveContactDuplicatesFilters,
  setPage,
  setSearch,
} from '@user/store/duplicates/WorkwaveContacts/slice'
import { Duplicate } from '@user/query/useDuplicateCheck'
import { ENTITY_TYPES } from '@sales-center/common'
import { useNavigate } from 'react-router-dom'
import { debounce } from 'lodash'

export interface Props {
  handleDuplicateManageClick?: () => void
  onDialogClose: () => void
}

export const useData = ({
  handleDuplicateManageClick,
  onDialogClose,
}: Props) => {
  const userGeneralPreferences = useSelector(userGeneralPreferencesList)
  const navigate = useNavigate()

  const dispatch = useAppDispatch()
  const {
    orderBy,
    page,
    perPage: rowsPerPage,
    resultCount,
    duplicates,
    searchText: search,
  } = useSelector(selectWorkWaveContactDuplicatesFilters)

  const [searchText, setSearchText] = useState<string>(search)

  const debouncedDispatch = useMemo(
    () =>
      debounce((val: string) => {
        dispatch(setSearch(val))
        dispatch(setPage(1))
      }, 500),
    [dispatch],
  )

  const handleSearchChange = (
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    setSearchText(e.target.value)
    debouncedDispatch(e.target.value)
  }

  const densityModeGeneralPreference = userGeneralPreferences?.find(
    (row) =>
      row?.type == IUserPreferenceGeneralListItemType.WorkWaveDensityMode,
  )

  const pageSizeGeneralPreference = userGeneralPreferences?.find(
    (row) => row?.type == IUserPreferenceGeneralListItemType.WorkWavePageSize,
  )

  const perPage = Number(pageSizeGeneralPreference?.value || rowsPerPage)
  const stringValue = densityModeGeneralPreference?.value ?? 'standard'
  const densityMode = stringValue as GridDensity

  const onDensityChange = (density: string) => {
    if (densityMode != density) {
      dispatch(
        saveGeneralPreferences({
          type: IUserPreferenceGeneralListItemType.WorkWaveDensityMode,
          value: density,
        }),
      )
    }
  }

  const onStateChange = (state: GridState) => {
    if (state.density) {
      onDensityChange(state.density.value)
    }
  }

  const onClickManage = (duplicate: Duplicate) => {
    if (handleDuplicateManageClick) {
      handleDuplicateManageClick()
      onDialogClose()
    }
    navigate(
      `/${
        duplicate.entityType === ENTITY_TYPES.Lead ? 'leads' : 'opportunities'
      }/${duplicate.id?.toString().split('-')[0]}`,
    )
  }

  return {
    duplicatesCount: resultCount,
    orderBy,
    page,
    perPage,
    densityMode,
    onStateChange,
    handleSearchChange,
    searchText,
    tableRows: duplicates,
    onClickManage,
  }
}
